import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import MicDictation from "../components/MicDictation";

const STEPS = [
  {
    key: "identity",
    overline: "Volume I · Who You Are",
    title: "Before we begin, name yourself.",
    hint: "Nothing is stored beyond this single session.",
    fields: [
      { name: "name", label: "Name", placeholder: "The name you answer to…", type: "text" },
      { name: "age", label: "Age", placeholder: "Years walked…", type: "text" },
      { name: "sex", label: "Sex", placeholder: "Female / Male", type: "text" },
    ],
  },
  {
    key: "traumas",
    overline: "Volume II · What You Carry",
    title: "Tell the Matrix, in order, what wounded you.",
    hint: "Required. Chronologically — earliest to most recent. You don't need to be tidy. Just honest.",
    fields: [
      {
        name: "traumas",
        label: "Traumas",
        placeholder: "At age 7 my father left. At age 12 I was sexually molested by a neighbor. I recently got a divorce…",
        type: "textarea",
        rows: 9,
      },
    ],
  },
  {
    key: "symptoms",
    overline: "Volume III · How It Lives In You",
    title: "What does it look like today?",
    hint: "Depression, anxiety, insomnia, hypervigilance, numbness — name it plainly.",
    fields: [
      {
        name: "symptoms",
        label: "Symptoms",
        placeholder: "Depression, anxiety, intrusive thoughts, I feel unappreciated, I feel bitter, I feel angry, I judge people…",
        type: "textarea",
        rows: 5,
      },
      {
        name: "family_pattern_history",
        label: "Did your father, mother, or grandparents carry these same kinds of symptoms or wounds — or did they carry the polar opposite?",
        placeholder:
          "Examples:\n• Father struggled with the same addiction.\n• Mother carried the same anxiety, but quieter.\n• Grandfather had the same rage.\n• Father was chaotic and absent; I overcorrected and became rigid and over-controlling.\n• Mother was cold and distant; I became smothering and over-affectionate as a parent.\n\nLeave blank if none of this applies.",
        type: "textarea",
        rows: 5,
      },
      {
        name: "diagnosis",
        label: "Any psychological diagnosis",
        placeholder: "PTSD · CPTSD · MDD · N/A",
        type: "text",
      },
      {
        name: "physical_diagnosis",
        label: "Any physical illness diagnosis",
        placeholder: "Diabetes · MS · cancer · chronic pain · autoimmune · N/A",
        type: "text",
      },
    ],
  },
  {
    key: "healing",
    overline: "Volume IV · Where Light Has Entered",
    title: "Where has true healing already touched you?",
    hint: "Anywhere there has been forgiveness, surrender, or healing — however small. This is the seed.",
    fields: [
      {
        name: "healing",
        label: "Healing",
        placeholder: "Examples:\n• I surrendered all the pain to Jesus from the loss of my father.\n• I renounced the lie that I wasn't valuable that came in after I was raped.\n• I no longer had negative feelings toward myself or my husband about what he did.\n• The day I forgave my father in the hospital room…",
        type: "textarea",
        rows: 8,
      },
    ],
  },
  {
    key: "music",
    overline: "Volume V · The Sound of Restoration",
    title: "What music meets you where you are?",
    hint: "One genre, or a blend. The Matrix will prescribe a playlist in your frequency.",
    fields: [
      {
        name: "music_genre",
        label: "Music Genre Preference",
        placeholder: "Worship · Gospel · Lo-fi · Jazz · Hip-hop · Classical…",
        type: "text",
      },
      {
        name: "reading_level",
        label: "Reading Level",
        type: "select",
        options: [
          { value: "standard", label: "Standard — full pastoral voice" },
          { value: "elementary", label: "Elementary — plain, simple language" },
        ],
        helper:
          "Choose Elementary for short sentences and everyday words while keeping the same depth.",
      },
    ],
  },
];

const INITIAL = {
  name: "", age: "", sex: "",
  traumas: "", symptoms: "", family_pattern_history: "",
  diagnosis: "", physical_diagnosis: "",
  healing: "", music_genre: "",
  reading_level: "standard",
  deceased_in_traumas: false,
  deceased_persons_named: "",
  has_fasted_before: "",
};

export default function Intake() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(INITIAL);
  const [error] = useState("");
  const navigate = useNavigate();

  const current = STEPS[step];

  // Feb 2026 (Scarlett 24 follow-up): every Volume step floats to the
  // top when the user advances or goes back. On a phone-shaped page
  // the previous step's tall textareas often left the next step's
  // heading well below the fold; this scrolls the new step's title
  // back into view so the recipient meets each Volume from its top.
  useEffect(() => {
    // Use behavior: "auto" (instant) so the new step appears already
    // at the top — a smooth scroll would briefly show the bottom of
    // the new step's content before settling, which reads as jank.
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  }, [step]);

  const canAdvance = current.fields.every((f) => {
    // Only "traumas" is mandatory. All other fields are optional.
    if (f.name === "traumas") return (form.traumas || "").trim().length > 0;
    return true;
  });

  const onChange = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

  const next = () => {
    if (!canAdvance) return;
    if (step < STEPS.length - 1) {
      setStep((s) => s + 1);
    } else {
      const payload = {
        ...form,
        diagnosis: (form.diagnosis || "").trim() || "N/A",
        physical_diagnosis: (form.physical_diagnosis || "").trim() || "N/A",
      };
      navigate("/generating", { state: { intake: payload } });
    }
  };

  const prev = () => (step > 0 ? setStep((s) => s - 1) : navigate("/"));

  return (
    <main className="relative min-h-screen flex flex-col" data-testid="intake-page">
      {/* FULL-BLEED atmospheric DNA + sacred geometry */}
      <div className="fixed inset-0 z-0 pointer-events-none" aria-hidden="true">
        <div className="absolute inset-0 bg-center bg-no-repeat bg-cover opacity-30"
             style={{
               backgroundImage: "url('/visuals/art_dna_geometry.png')",
               backgroundSize: "min(120vh, 120vw)",
             }} />
        <div className="absolute inset-0"
             style={{
               background:
                 "radial-gradient(ellipse at center, rgba(5,8,24,0.55) 0%, rgba(5,8,24,0.85) 55%, rgba(5,8,24,0.96) 100%)",
             }} />
      </div>

      <header className="relative z-20 px-6 sm:px-12 md:px-20 py-7 flex items-center justify-between">
        <Link to="/" className="font-cinzel text-[0.7rem] sm:text-xs text-[#FFE7A0]" data-testid="intake-brand">
          THE RESTORATION MATRIX
        </Link>
        <span className="font-cinzel text-[0.65rem] text-[#F0E6D2]/55" data-testid="intake-progress">
          {String(step + 1).padStart(2, "0")} / {String(STEPS.length).padStart(2, "0")}
        </span>
      </header>

      <div className="relative z-20 px-6 sm:px-12 md:px-20 -mt-2 mb-2 flex justify-center">
        <span className="rm-privacy-inline" data-testid="intake-privacy-disclaimer">
          <span className="rm-privacy-dot" aria-hidden="true" />
          Privacy guaranteed — no record of anything is saved, no account needed.
        </span>
      </div>

      <section className="relative z-10 flex-1 flex items-start md:items-center">
        <div className="w-full max-w-3xl mx-auto px-6 sm:px-12 md:px-16 py-10 md:py-16">
          <AnimatePresence mode="wait">
            <motion.div
              key={current.key}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
              data-testid={`intake-step-${current.key}`}
            >
              <div className="overline mb-6" data-testid="intake-step-overline">
                {current.overline}
              </div>
              <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05] text-[#F0E6D2] mb-5">
                {current.title}
              </h2>
              <p className="text-[#F0E6D2]/55 italic mb-4 text-base sm:text-lg font-body font-light max-w-2xl">
                {current.hint}
              </p>
              {current.key !== "traumas" && (
                <p className="text-[#F0E6D2]/35 mb-10 text-xs font-body font-light italic">
                  Optional — leave blank to skip.
                </p>
              )}
              {current.key === "traumas" && (
                <div className="mb-10" />
              )}

              {["traumas", "symptoms", "healing"].includes(current.key) && (
                <div
                  className="mb-8 border border-[#FFE7A0]/30 bg-[#FFE7A0]/5 px-5 py-3 text-sm text-[#F0E6D2]/90 font-body font-light leading-relaxed"
                  data-testid={`intake-quality-note-${current.key}`}
                >
                  <span className="font-cinzel text-[0.7rem] tracking-[0.22em] uppercase text-[#FFE7A0] mr-2">Note</span>
                  The more you share here, the more thorough and personal the packet. Voice input is welcome — tap the mic beside any field.
                </div>
              )}

              <div className="space-y-10">
                {current.fields.map((f) => (
                  <div key={f.name}>
                    <label className="overline block mb-3 text-[0.65rem]" htmlFor={`field-${f.name}`}>
                      {f.label}
                    </label>
                    {f.type === "textarea" ? (
                      <div className="relative">
                        <textarea
                          id={`field-${f.name}`}
                          data-testid={`intake-${f.name}-input`}
                          className="rm-input rm-textarea pr-12"
                          placeholder={f.placeholder}
                          rows={f.rows || 6}
                          value={form[f.name]}
                          onChange={(e) => onChange(f.name, e.target.value)}
                        />
                        <MicDictation
                          value={form[f.name]}
                          onTranscript={(next) => onChange(f.name, next)}
                          testId={`intake-${f.name}-mic`}
                        />
                      </div>
                    ) : f.type === "select" ? (
                      <div>
                        <select
                          id={`field-${f.name}`}
                          data-testid={`intake-${f.name}-input`}
                          className="rm-input"
                          value={form[f.name]}
                          onChange={(e) => onChange(f.name, e.target.value)}
                        >
                          {f.options.map((opt) => (
                            <option key={opt.value} value={opt.value} className="bg-[#0a0f24] text-[#F0E6D2]">
                              {opt.label}
                            </option>
                          ))}
                        </select>
                        {f.helper && (
                          <p className="mt-2 text-xs text-[#F0E6D2]/50 font-body font-light italic" data-testid={`intake-${f.name}-helper`}>
                            {f.helper}
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="relative">
                        <input
                          id={`field-${f.name}`}
                          data-testid={`intake-${f.name}-input`}
                          type="text"
                          className="rm-input pr-12"
                          placeholder={f.placeholder}
                          value={form[f.name]}
                          onChange={(e) => onChange(f.name, e.target.value)}
                        />
                        <MicDictation
                          value={form[f.name]}
                          onTranscript={(next) => onChange(f.name, next)}
                          testId={`intake-${f.name}-mic`}
                        />
                      </div>
                    )}
                  </div>
                ))}

                {/* Fasting-experience fail-safe — only on the Music
                    step (the final preferences step before submission).
                    Three-state: Yes / No / blank-default. "No" forces
                    the gentle 1-day on-ramp regardless of how severe
                    the rest of the intake reads; "Yes" lets the
                    Matrix recommend the standard graduated path (1
                    → 3 → 7 days based on severity). Leaving it
                    unselected falls back to free-text detection in
                    the trauma narrative. */}
                {current.key === "music" && (
                  <div className="border border-[#FFE7A0]/20 px-5 py-4 bg-[#FFE7A0]/[0.03]" data-testid="intake-fasting-block">
                    <div className="text-sm font-body font-light text-[#F0E6D2]/85 leading-relaxed">
                      Have you fasted before?
                      <span className="block text-xs text-[#F0E6D2]/45 italic mt-1">
                        Optional. If this is your first time, the Matrix will recommend a gentle one-day water fast as the starting step — no pressure for more.
                      </span>
                    </div>
                    <div className="mt-3 flex gap-6">
                      <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                          type="radio"
                          name="has_fasted_before"
                          value="yes"
                          data-testid="intake-has_fasted_before-yes"
                          checked={form.has_fasted_before === "yes"}
                          onChange={() => onChange("has_fasted_before", "yes")}
                          className="h-4 w-4 accent-[#FFE7A0] cursor-pointer"
                        />
                        <span className="text-sm font-body font-light text-[#F0E6D2]/85">Yes, I have</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                          type="radio"
                          name="has_fasted_before"
                          value="no"
                          data-testid="intake-has_fasted_before-no"
                          checked={form.has_fasted_before === "no"}
                          onChange={() => onChange("has_fasted_before", "no")}
                          className="h-4 w-4 accent-[#FFE7A0] cursor-pointer"
                        />
                        <span className="text-sm font-body font-light text-[#F0E6D2]/85">No, never</span>
                      </label>
                      {form.has_fasted_before && (
                        <button
                          type="button"
                          data-testid="intake-has_fasted_before-clear"
                          onClick={() => onChange("has_fasted_before", "")}
                          className="text-xs text-[#F0E6D2]/40 italic hover:text-[#F0E6D2]/70 underline underline-offset-2"
                        >
                          clear
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Deceased fail-safe — only on the Traumas step */}
                {current.key === "traumas" && (
                  <div className="border border-[#FFE7A0]/20 px-5 py-4 bg-[#FFE7A0]/[0.03]" data-testid="intake-deceased-block">
                    <label className="flex items-start gap-3 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        data-testid="intake-deceased_in_traumas-input"
                        checked={form.deceased_in_traumas}
                        onChange={(e) => onChange("deceased_in_traumas", e.target.checked)}
                        className="mt-[3px] h-4 w-4 accent-[#FFE7A0] cursor-pointer"
                      />
                      <span className="text-sm font-body font-light text-[#F0E6D2]/85 leading-relaxed">
                        Some of the people in my story have passed away.
                        <span className="block text-xs text-[#F0E6D2]/45 italic mt-1">
                          Optional. Helps the Matrix soften prayer language for those who can no longer be reached on this side of eternity.
                        </span>
                      </span>
                    </label>
                    {form.deceased_in_traumas && (
                      <div className="mt-4 pl-7" data-testid="intake-deceased_persons_named-block">
                        <label className="overline block mb-2 text-[0.6rem]" htmlFor="field-deceased_persons_named">
                          Who has passed?
                        </label>
                        <input
                          id="field-deceased_persons_named"
                          data-testid="intake-deceased_persons_named-input"
                          type="text"
                          className="rm-input"
                          placeholder="e.g. father, brother, grandmother"
                          value={form.deceased_persons_named}
                          onChange={(e) => onChange("deceased_persons_named", e.target.value)}
                        />
                        <p className="mt-2 text-xs text-[#F0E6D2]/45 italic">
                          A short list, comma-separated. Leave blank if you'd rather not name anyone.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {error && (
                <p className="mt-8 text-sm text-[#FFE7A0]/80 italic" data-testid="intake-error">
                  {error}
                </p>
              )}

              <div className="mt-16 flex items-center justify-between">
                <button type="button" className="rm-btn-ghost" onClick={prev} data-testid="intake-back-btn">
                  ← {step === 0 ? "Return" : "Back"}
                </button>
                <button type="button" className="rm-btn" disabled={!canAdvance} onClick={next} data-testid="intake-next-btn">
                  {step === STEPS.length - 1 ? "Compile Packet" : "Continue"}
                </button>
              </div>

              {/* Engine signature — final step only */}
              {step === STEPS.length - 1 && (
                <p className="mt-10 text-center font-cinzel text-[0.6rem] uppercase tracking-[0.3em] text-[#F0E6D2]/40 italic" data-testid="intake-engine-signature">
                  Compiled by the Matrix · ~90 seconds
                </p>
              )}
            </motion.div>
          </AnimatePresence>

          <div className="mt-20 h-[1px] w-full bg-[#FFE7A0]/10 relative" data-testid="intake-progress-bar">
            <motion.div
              className="absolute top-0 left-0 h-[1px] bg-[#FFE7A0]"
              style={{ boxShadow: "0 0 10px rgba(255, 231, 160,0.5)" }}
              initial={false}
              animate={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
              transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
            />
          </div>
        </div>
      </section>
    </main>
  );
}
